import React, { useState } from 'react';

import { EmpresaFiscal } from '../../../services/ativarEmissaoService';
import * as S from '../styles';

interface Step4Props {
  company: EmpresaFiscal | null;
  loading: boolean;
  onActivate: (ambiente: number) => Promise<void> | void;
  onDeactivate: () => Promise<void> | void;
}

/**
 * Passo 4 — Ativar ambiente (Story 1-7 / AC9/AC10). Confirma o ambiente fiscal
 * e dispara PATCH /empresa/:id/fiscal { emissao_habilitada: true }. Como o
 * provisionamento (tenant+key) já ocorreu no passo 1 (ordem técnica D5), aqui
 * apenas confirmamos/ajustamos o ambiente e finalizamos a habilitação.
 *
 * Também é daqui que a emissão é DESLIGADA (`emissao_habilitada: false`), com
 * confirmação em dois passos: é a ação que faz o PDV parar de emitir NFC-e.
 */
const Step4AtivarAmbiente: React.FC<Step4Props> = ({
  company,
  loading,
  onActivate,
  onDeactivate,
}) => {
  const [ambiente, setAmbiente] = useState<string>(
    company?.ambiente_fiscal || '2',
  );
  const [confirmandoDesativar, setConfirmandoDesativar] = useState(false);

  return (
    <S.StepCard>
      <h3>4. Ativar ambiente</h3>

      <p>
        Confirme o ambiente de emissão e finalize a ativação. A emissão só fica
        habilitada quando o provisionamento (tenant, chave e webhook) está
        concluído.
      </p>

      <div className="field">
        <label htmlFor="ambiente-final">Ambiente de emissão</label>
        <select
          id="ambiente-final"
          value={ambiente}
          onChange={(e) => setAmbiente(e.target.value)}
        >
          <option value="1">Produção</option>
          <option value="2">Homologação</option>
        </select>
      </div>

      {company?.emissao_habilitada && (
        <S.StatusBadge tone="ok">Emissão habilitada</S.StatusBadge>
      )}

      {company?.csc_status !== 'configurado' && (
        <S.Warning>
          O CSC ainda não está configurado. A ativação funciona, mas as notas
          serão <strong>rejeitadas pela SEFAZ</strong> até o CSC ser informado no
          passo <strong>3. CSC</strong>.
        </S.Warning>
      )}

      <S.Button
        type="button"
        onClick={() => onActivate(Number(ambiente))}
        disabled={loading}
      >
        {loading ? 'Ativando…' : 'Ativar emissão'}
      </S.Button>

      {company?.emissao_habilitada && (
        <S.DangerZone>
          <strong>Desabilitar emissão fiscal</strong>
          <p>
            O PDV para de emitir NFC-e para esta empresa. O certificado, o CSC e
            a chave da atac-notas continuam guardados — para voltar a emitir
            basta ativar novamente, sem refazer a configuração.
          </p>

          {confirmandoDesativar ? (
            <div className="confirm_actions">
              <S.Button
                type="button"
                variant="danger"
                onClick={async () => {
                  await onDeactivate();
                  setConfirmandoDesativar(false);
                }}
                disabled={loading}
              >
                {loading ? 'Desabilitando…' : 'Confirmar desabilitação'}
              </S.Button>
              <S.Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmandoDesativar(false)}
                disabled={loading}
              >
                Cancelar
              </S.Button>
            </div>
          ) : (
            <S.Button
              type="button"
              variant="danger"
              onClick={() => setConfirmandoDesativar(true)}
              disabled={loading}
            >
              Desabilitar emissão
            </S.Button>
          )}
        </S.DangerZone>
      )}
    </S.StepCard>
  );
};

export default Step4AtivarAmbiente;
